"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  TrendingStocksCardData,
  TrendingStocksDurationFilter,
  TrendingStocksEntityMap,
  TrendingStocksPrimaryTab,
} from "../types";
import { fetchTrendingStocksList } from "../utils/trendingStocksApi";
import { resolveEntityForSelection } from "../utils/trendingStocksHelpers";

interface UseTrendingStocksPlpProps {
  initialItems: TrendingStocksCardData[];
  initialTotalCount: number;
  initialShowMore: boolean;
  initialTab: TrendingStocksPrimaryTab;
  initialDuration: TrendingStocksDurationFilter;
  entityMap?: TrendingStocksEntityMap;
  defaultEntity?: string;
  limit?: number;
  initialExpandedCards?: number;
}

interface FetchSelectionArgs {
  tab: TrendingStocksPrimaryTab;
  duration: TrendingStocksDurationFilter;
  page: number;
  append: boolean;
}

export function useTrendingStocksPlp({
  initialItems,
  initialTotalCount,
  initialShowMore,
  initialTab,
  initialDuration,
  entityMap,
  defaultEntity = "all",
  limit = 10,
  initialExpandedCards = 1,
}: Readonly<UseTrendingStocksPlpProps>) {
  const [activeTab, setActiveTab] =
    useState<TrendingStocksPrimaryTab>(initialTab);
  const [activeDuration, setActiveDuration] =
    useState<TrendingStocksDurationFilter>(initialDuration);
  const [items, setItems] = useState<TrendingStocksCardData[]>(initialItems);
  const [totalCount, setTotalCount] = useState<number>(initialTotalCount);
  const [showMore, setShowMore] = useState<boolean>(initialShowMore);
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const selectionRef = useRef(`${initialTab}-${initialDuration}`);

  const fetchSelection = useCallback(
    async ({ tab, duration, page, append }: FetchSelectionArgs) => {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const entity = resolveEntityForSelection(entityMap, tab, duration, defaultEntity);

      const response = await fetchTrendingStocksList({
        entity,
        tab,
        duration,
        page,
        limit,
        initialExpandedCards,
      });

      if (!response) {
        setHasError(true);
        setShowMore(false);
        if (!append) {
          setItems([]);
          setTotalCount(0);
        }
      } else {
        setHasError(false);
        setItems((prevItems) =>
          append ? [...prevItems, ...response.items] : response.items,
        );
        setTotalCount(response.totalCount);
        setShowMore(response.showMore);
        setPage(page);
      }

      setIsLoading(false);
      setIsLoadingMore(false);
    },
    [defaultEntity, entityMap, initialExpandedCards, limit],
  );

  useEffect(() => {
    const selectionKey = `${activeTab}-${activeDuration}`;
    if (selectionRef.current === selectionKey) {
      return;
    }

    selectionRef.current = selectionKey;
    void fetchSelection({
      tab: activeTab,
      duration: activeDuration,
      page: 1,
      append: false,
    });
  }, [activeDuration, activeTab, fetchSelection]);

  const handleShowMore = useCallback(() => {
    if (!showMore || isLoading || isLoadingMore) return;
    void fetchSelection({
      tab: activeTab,
      duration: activeDuration,
      page: page + 1,
      append: true,
    });
  }, [activeDuration, activeTab, fetchSelection, isLoading, isLoadingMore, page, showMore]);

  const retryCurrentSelection = useCallback(() => {
    void fetchSelection({
      tab: activeTab,
      duration: activeDuration,
      page: 1,
      append: false,
    });
  }, [activeDuration, activeTab, fetchSelection]);

  return {
    activeTab,
    setActiveTab,
    activeDuration,
    setActiveDuration,
    items,
    totalCount,
    showMore,
    isLoading,
    isLoadingMore,
    hasError,
    handleShowMore,
    retryCurrentSelection,
  };
}

export default useTrendingStocksPlp;
